import { Link } from 'react-router-dom';
import { UserStatus, type ParentTeenSummary } from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { useParentOverview } from '@/features/parent/use-parent-overview';
import { ParentShell } from '@/features/parent/ParentShell';
import { EmptyState, StatTile } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  ArrowRightIcon,
  BadgeCheckIcon,
  BriefcaseIcon,
  MapPinIcon,
  ShieldCheckIcon,
  UsersIcon,
} from '@/components/ui/Icons';
import { presentApiError } from '@/lib/form-errors';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return `${parts[0]?.charAt(0) ?? ''}${parts.length > 1 ? (parts.at(-1)?.charAt(0) ?? '') : ''}`.toUpperCase();
}

/**
 * The guardian's home. [Parent journey overview]
 *
 * Ordered by urgency, not by feature: what is waiting on you, then how your
 * teens are doing, then the network that makes future decisions easier.
 */
export function ParentDashboardPage() {
  const { user } = useAuth();
  const overview = useParentOverview();
  const data = overview.data;

  const waiting = (data?.pendingJobApprovals ?? 0) + (data?.pendingTeenAccounts ?? 0);
  const firstName = user?.displayName.split(' ')[0] ?? 'there';

  return (
    <ParentShell
      title={`Good to see you, ${firstName}`}
      description={
        waiting > 0
          ? `${waiting} ${waiting === 1 ? 'decision is' : 'decisions are'} waiting on you.`
          : 'Nothing is waiting on you right now.'
      }
    >
      {overview.isPending && (
        <div className="space-y-6" aria-busy="true">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((n) => (
              <Skeleton key={n} className="h-28" />
            ))}
          </div>
          <Skeleton className="h-40" />
        </div>
      )}

      {overview.isError && (
        <div className="rounded-2xl border border-line bg-white p-8 text-center shadow-soft">
          <p className="text-sm text-slate-500">
            {presentApiError(overview.error, 'Could not load your console').message}
          </p>
          <Button className="mt-5" onClick={() => void overview.refetch()}>
            Try again
          </Button>
        </div>
      )}

      {data && (
        <div className="space-y-8">
          {/* What needs you */}
          <section>
            <h2 className="sr-only">At a glance</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="Job approvals"
                value={data.pendingJobApprovals}
                tone={data.pendingJobApprovals > 0 ? 'attention' : 'neutral'}
                hint={
                  data.pendingJobApprovals > 0
                    ? 'A teen cannot apply until you answer'
                    : 'Nothing waiting'
                }
                icon={<ShieldCheckIcon className="size-4 text-slate-400" />}
              />
              <StatTile
                label="Teen accounts"
                value={data.pendingTeenAccounts}
                tone={data.pendingTeenAccounts > 0 ? 'attention' : 'neutral'}
                hint={
                  data.pendingTeenAccounts > 0 ? 'Awaiting your approval' : 'All teens approved'
                }
                icon={<UsersIcon className="size-4 text-slate-400" />}
              />
              <StatTile
                label="Trusted employers"
                value={data.trustedEmployers}
                tone={data.trustedEmployers > 0 ? 'good' : 'neutral'}
                hint="Vouched for by you"
                icon={<BadgeCheckIcon className="size-4 text-slate-400" />}
              />
              <StatTile
                label="Guardians you know"
                value={data.connectedParents}
                hint={
                  data.incomingConnectionRequests > 0
                    ? `${data.incomingConnectionRequests} request awaiting you`
                    : 'Their trust shows on job cards'
                }
                tone={data.incomingConnectionRequests > 0 ? 'attention' : 'neutral'}
                icon={<UsersIcon className="size-4 text-slate-400" />}
              />
            </div>
          </section>

          {/* Act on it */}
          {waiting > 0 && (
            <section className="rounded-2xl border border-sun-200 bg-sun-50 p-5 sm:p-6">
              <h2 className="text-base font-bold text-sun-800">Waiting on you</h2>
              <ul className="mt-4 space-y-2">
                {data.pendingJobApprovals > 0 && (
                  <li>
                    <Link
                      to="/approvals"
                      className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-ink transition hover:bg-white/70"
                    >
                      <span>
                        {data.pendingJobApprovals} job{' '}
                        {data.pendingJobApprovals === 1 ? 'request' : 'requests'} to approve
                      </span>
                      <ArrowRightIcon className="size-4 text-brand-600" />
                    </Link>
                  </li>
                )}
                {data.pendingTeenAccounts > 0 && (
                  <li>
                    <Link
                      to="/guardian"
                      className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-ink transition hover:bg-white/70"
                    >
                      <span>
                        {data.pendingTeenAccounts} teen{' '}
                        {data.pendingTeenAccounts === 1 ? 'account' : 'accounts'} to approve
                      </span>
                      <ArrowRightIcon className="size-4 text-brand-600" />
                    </Link>
                  </li>
                )}
              </ul>
            </section>
          )}

          {/* Your teens */}
          <section>
            <div className="mb-4 flex items-baseline justify-between gap-3">
              <h2 className="text-base font-bold text-ink">Your teens</h2>
              <span className="text-xs font-semibold text-slate-400">
                {data.teens.length} linked
              </span>
            </div>

            {data.teens.length === 0 ? (
              <EmptyState icon={<UsersIcon className="size-6" />} title="No teens linked yet">
                When a teen signs up and names your email address as their guardian, they appear
                here for you to approve.
              </EmptyState>
            ) : (
              <ul className="grid gap-4 lg:grid-cols-2">
                {data.teens.map((teen) => (
                  <TeenCard key={teen.id} teen={teen} />
                ))}
              </ul>
            )}
          </section>

          {/* Network */}
          <section className="rounded-2xl border border-line bg-white p-5 shadow-soft sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-bold text-ink">Your trusted network</h2>
                <p className="mt-1 max-w-lg text-sm leading-relaxed text-slate-500">
                  Employers you vouch for, and other guardians you know. Your teen sees this on
                  every job card — and so does every guardian connected to you.
                </p>
              </div>
              <Link
                to="/network"
                className="inline-flex items-center gap-1.5 rounded-xl border border-line bg-white px-4 py-2.5 text-sm font-semibold text-ink transition hover:border-brand-300 hover:bg-brand-50"
              >
                Manage
                <ArrowRightIcon className="size-4" />
              </Link>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Badge tone="safe" icon={<BadgeCheckIcon className="size-3.5" />}>
                {data.trustedEmployers} trusted
              </Badge>
              {data.blockedEmployers > 0 && (
                <Badge tone="neutral">{data.blockedEmployers} blocked</Badge>
              )}
              <Badge tone="brand" icon={<UsersIcon className="size-3.5" />}>
                {data.connectedParents} guardians
              </Badge>
            </div>
          </section>
        </div>
      )}
    </ParentShell>
  );
}

function TeenCard({ teen }: { teen: ParentTeenSummary }) {
  const blocked = teen.status !== UserStatus.ACTIVE;

  return (
    <li className="rounded-2xl border border-line bg-white p-5 shadow-soft transition hover:border-brand-200">
      <div className="flex items-start gap-4">
        {teen.photoUrl ? (
          <img src={teen.photoUrl} alt="" className="size-12 rounded-xl object-cover" />
        ) : (
          <span
            aria-hidden="true"
            className="grid size-12 shrink-0 place-items-center rounded-xl bg-slate-100 text-sm font-extrabold text-slate-500"
          >
            {initials(teen.name)}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-bold text-ink">{teen.name}</h3>
            <Badge tone={blocked ? 'sun' : 'safe'}>
              {blocked ? 'Awaiting approval' : 'Active'}
            </Badge>
            {!teen.verified && <Badge tone="neutral">Link unconfirmed</Badge>}
          </div>

          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="capitalize">{teen.relationshipType}</span>
            {teen.city && (
              <span className="inline-flex items-center gap-1">
                <MapPinIcon className="size-3.5 text-slate-400" />
                {[teen.city, teen.state].filter(Boolean).join(', ')}
              </span>
            )}
            {teen.isMinor && <span>Under 18</span>}
          </p>
        </div>
      </div>

      <dl className="mt-4 grid grid-cols-3 gap-2 border-t border-line pt-4 text-center">
        <div>
          <dt className="text-[0.65rem] font-semibold tracking-wide text-slate-400 uppercase">
            Applications
          </dt>
          <dd className="mt-0.5 text-lg font-extrabold text-ink">{teen.openApplications}</dd>
        </div>
        <div>
          <dt className="text-[0.65rem] font-semibold tracking-wide text-slate-400 uppercase">
            Active jobs
          </dt>
          <dd className="mt-0.5 text-lg font-extrabold text-ink">{teen.activeAssignments}</dd>
        </div>
        <div>
          <dt className="text-[0.65rem] font-semibold tracking-wide text-slate-400 uppercase">
            Needs you
          </dt>
          <dd
            className={`mt-0.5 text-lg font-extrabold ${
              teen.pendingConsents > 0 ? 'text-sun-700' : 'text-ink'
            }`}
          >
            {teen.pendingConsents}
          </dd>
        </div>
      </dl>

      {teen.pendingConsents > 0 && (
        <Link
          to="/approvals"
          className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700"
        >
          <BriefcaseIcon className="size-4" />
          Review {teen.pendingConsents} job {teen.pendingConsents === 1 ? 'request' : 'requests'}
        </Link>
      )}
    </li>
  );
}
